import { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { PageLoader } from "@/components/ui/spinner";
import { hasCapability, useSession } from "@/lib/auth";

type Props = { children: ReactNode };

export function RequireAuth({ children }: Props) {
  const session = useSession();
  const location = useLocation();

  if (session.isLoading) {
    return <PageLoader className="min-h-screen" />;
  }

  if (!session.data?.user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const u = session.data.user;
  const elevated = u.is_platform_admin || u.org_role === "org_admin";
  const needsStaging = !u.organization_id && !elevated;
  if (needsStaging && location.pathname !== "/staging") {
    return <Navigate to="/staging" replace />;
  }

  return <>{children}</>;
}

export function RequireCompanyAdmin({ children }: Props) {
  const session = useSession();
  const location = useLocation();

  if (session.isLoading) {
    return <PageLoader className="min-h-screen" />;
  }

  const user = session.data?.user;
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (!user.is_platform_admin && user.org_role !== "org_admin") {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

export function RequireCapability({
  capability,
  children,
}: Props & { capability: string }) {
  const session = useSession();
  const location = useLocation();

  if (session.isLoading) {
    return <PageLoader className="min-h-screen" />;
  }

  const user = session.data?.user;
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (!hasCapability(user, capability)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

export function RequirePlatformAdmin({ children }: Props) {
  const session = useSession();
  const location = useLocation();

  if (session.isLoading) {
    return <PageLoader className="min-h-screen" />;
  }

  const user = session.data?.user;
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (!user.is_platform_admin) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}
